import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';
import PlusIcon from 'svg/PlusIcon';

import { VALUE_NAMES } from '../constants';

// The Payer Name / Member ID row shown fixed below the coverage tabs (OLIO-3116, replacing the
// details card that used to open every slideout state). Reads the check inputs off the workflow
// values and offers the Edit entry point.

interface Props {
  workflow: Workflow;
  // Undefined when the user may not edit the referral, which hides the link (OLIO-2615).
  onEditDetails?: () => void;
  // The coverage whose details these are; null on a referral with no coverages.
  facetId: string | null;
  // A coverage tab nobody has filled yet (the empty Secondary). Its payer and member ID are blank,
  // never the run-level `plan_type` a null facetId would otherwise fall back to.
  emptySlot?: boolean;
  // The settled result's active/inactive call (OLIO-3116) — undefined for every other slideout
  // state (running, pending, select-payer, error, …), which renders no pill at all. Replaces the
  // green/red banner that used to sit atop the result set.
  coverageStatus?: 'active' | 'inactive';
  // Undefined when benefits can't be documented by hand right now (no grant, or a check is running).
  onAddManually?: () => void;
}

export default function EligibilityDetailsHeader({
  workflow,
  onEditDetails,
  facetId,
  emptySlot = false,
  coverageStatus,
  onAddManually,
}: Props) {
  // The COVERAGE's payer, never a referral-wide one: reading a run-level payer here would put the
  // primary's payer next to the secondary's member ID, the mismatch the no-fallback rule exists to
  // prevent. Only a referral with no coverages at all (facetId null) falls back to `plan_type`,
  // because that referral predates per-coverage verification and its own is run-level. Referral
  // intake stopped capturing that value in OLIO-2890, so nothing new writes it.
  const payerName = emptySlot
    ? undefined
    : facetId
      ? workflow.valueFor(VALUE_NAMES.PAYER_NAME, { facetId })?.value
      : workflow.valueFor(VALUE_NAMES.PLAN_TYPE)?.value;
  const memberId = emptySlot ? undefined : workflow.valueFor(VALUE_NAMES.MEMBER_ID, { facetId })?.value;
  // Referral-level like name (patient DOB doesn't vary by coverage) — kept in the bar so the
  // "No Results Found" copy's "verify member ID, name, and date of birth" has a DOB on screen to
  // check against (name lives in the slideout title).
  const dob = simpleDate(workflow.valueFor(VALUE_NAMES.PATIENT_DOB)?.value);

  return (
    <Row data-testid='eligibility-details-header'>
      <LeftGroup>
        <Details>
          Payer Name: <Value>{payerName || '—'}</Value>
          &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; Member ID: <Value>{memberId || '—'}</Value>
          &nbsp;&nbsp;&nbsp;&middot;&nbsp;&nbsp; DOB: <Value>{dob || '—'}</Value>
        </Details>
        {coverageStatus && (
          <CoveragePill $tone={coverageStatus} data-testid='coverage-pill'>
            {coverageStatus === 'active' ? 'Active Coverage' : 'No Active Coverage'}
          </CoveragePill>
        )}
      </LeftGroup>
      <Links>
        {onAddManually && (
          <EditLink type='button' onClick={onAddManually} data-testid='add-benefits-manually'>
            <PlusIcon color={colors.blue100} width={10} height={10} />
            Add Benefits Manually
          </EditLink>
        )}
        {onAddManually && onEditDetails && <Divider />}
        {onEditDetails && (
          <EditLink type='button' onClick={onEditDetails} data-testid='edit-details'>
            <PencilIcon color={colors.blue100} width={12} height={12} />
            Edit
          </EditLink>
        )}
      </Links>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const Details = styled(BodySmall)`
  color: ${colors.black50};
`;

const CoveragePill = styled.span<{ $tone: 'active' | 'inactive' }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 48px;
  font-size: 10px;
  font-weight: 500;
  color: ${colors.white};
  background: ${({ $tone }) => ($tone === 'active' ? colors.green100 : colors.red100)};
`;

const Value = styled.span`
  color: ${colors.black100};
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const Divider = styled.span`
  align-self: stretch;
  width: 1px;
  background: ${colors.black10};
`;

const EditLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  flex-shrink: 0;
`;
